import { useTheme } from "@mui/material";
import type { Editor } from "@tiptap/core";
import type { ImageNodeAttributes, SwatchColorOption } from "mui-tiptap";
import {
  MenuButtonBlockquote,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonEditLink,
  MenuButtonHighlightColor,
  MenuButtonHorizontalRule,
  MenuButtonImageUpload,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonTextColor,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  MenuSelectTextAlign,
  useRichTextEditorContext,
} from "mui-tiptap";
import { trackPosition } from "./htmlEditorPositions";
import type { HtmlEditorUploadedImage } from "./htmlEditorTypes";

// Swatches are persisted verbatim into saved HTML, so they stay theme-independent, not palette-derived.
const textColorSwatches: SwatchColorOption[] = [
  { value: "#000000", label: "Black" },
  { value: "#ffffff", label: "White" },
  { value: "#888888", label: "Grey" },
  { value: "#ff0000", label: "Red" },
  { value: "#ff9900", label: "Orange" },
  { value: "#ffff00", label: "Yellow" },
  { value: "#00d000", label: "Green" },
  { value: "#0000ff", label: "Blue" },
];

const highlightColorSwatches: SwatchColorOption[] = [
  { value: "#595959", label: "Dark grey" },
  { value: "#dddddd", label: "Light grey" },
  { value: "#ffa6a6", label: "Light red" },
  { value: "#ffd699", label: "Light orange" },
  // Plain yellow matches the browser default `mark`, as applied by Cmd+Shift+H.
  { value: "#ffff00", label: "Yellow" },
  { value: "#99cc99", label: "Light green" },
  { value: "#90c6ff", label: "Light blue" },
  { value: "#8085e9", label: "Light purple" },
];

const acceptedImageExtensions = ".jpg,.jpeg,.png,.webp";

const isTextFieldFocused = (): boolean =>
  document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLTextAreaElement;

const insertUploadedImages = (editor: Editor, position: number, images: HtmlEditorUploadedImage[]) => {
  if (editor.isDestroyed || images.length === 0) {
    return;
  }

  // The upload resolves later, so the caret is moved only when the user is not typing somewhere else meanwhile.
  const chain = editor.chain().insertContentAt(
    position,
    images.map(({ url, fileId, fileName }) => ({ type: "image", attrs: { src: url, alt: fileName, fileId } })),
    { updateSelection: !editor.isFocused }
  );

  const focusedChain = editor.isFocused || isTextFieldFocused() ? chain : chain.focus();
  focusedChain.run();
};

interface Props {
  onUploadImages?: (files: File[]) => Promise<HtmlEditorUploadedImage[]>;
}

const DefaultHtmlEditorControls = ({ onUploadImages }: Props) => {
  const theme = useTheme();
  const editor = useRichTextEditorContext();

  const handleUploadFiles = async (files: File[]): Promise<ImageNodeAttributes[]> => {
    if (!editor || !onUploadImages) {
      return [];
    }

    const insertPosition = trackPosition(editor, editor.state.selection.to);
    try {
      const images = await onUploadImages(files);
      insertUploadedImages(editor, insertPosition.current, images);
    } finally {
      insertPosition.stop();
    }
    return [];
  };

  return (
    <MenuControlsContainer>
      <MenuSelectHeading />
      <MenuDivider />

      <MenuButtonBold />
      <MenuButtonItalic />
      <MenuButtonUnderline />
      <MenuButtonStrikethrough />
      <MenuDivider />

      <MenuButtonTextColor defaultTextColor={theme.palette.text.primary} swatchColors={textColorSwatches} />
      <MenuButtonHighlightColor swatchColors={highlightColorSwatches} />
      <MenuDivider />

      <MenuSelectTextAlign />
      <MenuDivider />

      <MenuButtonOrderedList />
      <MenuButtonBulletedList />
      <MenuDivider />

      <MenuButtonEditLink />
      {onUploadImages && (
        <MenuButtonImageUpload onUploadFiles={handleUploadFiles} inputProps={{ accept: acceptedImageExtensions }} />
      )}
      <MenuDivider />

      <MenuButtonBlockquote />
      <MenuButtonHorizontalRule />
      <MenuDivider />

      <MenuButtonRemoveFormatting />
    </MenuControlsContainer>
  );
};

export default DefaultHtmlEditorControls;
